import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDialogCloseIconSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const emptyForm = {
  groupName: '',
  groupCode: '',
  description: '',
  status: 'ACTIVE',
};

const mapRow = (item, index) => ({
  id: item.propertyGroupId || index + 1,
  propertyGroupId: item.propertyGroupId || '',
  groupName: item.groupName || '',
  groupCode: item.groupCode || '',
  description: item.description || '',
  status: item.status || 'ACTIVE',
});

const PropertyGroup = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.PROPERTY_GROUP);
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const tenantId = localStorage.getItem('tenant_id') || '';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [mode, setMode] = useState('add');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const selectedRow = rows.find((row) => selectionModel.includes(row.id)) || null;
  const hasSelected = Boolean(selectedRow);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    };
  };

  const fetchGroups = async () => {
    if (!tenantId) {
      setRows([]);
      return;
    }
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/property-groups/by-tenant?tenant_id=${encodeURIComponent(tenantId)}`,
        { method: 'GET', headers: authHeaders() }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to fetch property groups: ${response.status}`);
      }
      const data = await response.json();
      setRows(Array.isArray(data) ? data.map(mapRow) : []);
    } catch (error) {
      console.error('Error fetching property groups:', error);
      setRows([]);
      showError(error.message || 'Failed to fetch property groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [tenantId]);

  const openAdd = () => {
    if (!canAdd) return;
    setMode('add');
    setForm(emptyForm);
    setOpenDialog(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    setMode('edit');
    setForm({
      groupName: selectedRow.groupName || '',
      groupCode: selectedRow.groupCode || '',
      description: selectedRow.description || '',
      status: selectedRow.status || 'ACTIVE',
    });
    setOpenDialog(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if ((mode === 'add' && !canAdd) || (mode === 'edit' && !canEdit)) return;
    if (!form.groupName.trim()) {
      showError('Group name is required');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        tenantId,
        groupName: form.groupName.trim(),
        groupCode: form.groupCode.trim(),
        description: form.description.trim(),
        status: form.status || 'ACTIVE',
      };
      const url = mode === 'edit'
        ? `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/property-groups/${selectedRow.propertyGroupId}`
        : `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/property-groups`;
      const response = await fetch(url, {
        method: mode === 'edit' ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to save property group: ${response.status}`);
      }
      setOpenDialog(false);
      setSelectionModel([]);
      showSuccess(mode === 'edit' ? 'Property group updated successfully' : 'Property group added successfully');
      await fetchGroups();
    } catch (error) {
      console.error('Error saving property group:', error);
      showError(error.message || 'Failed to save property group');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !selectedRow) return;
    try {
      setDeleteLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/property-groups/hard/${selectedRow.propertyGroupId}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      if (!response.ok && response.status !== 204) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to delete property group: ${response.status}`);
      }
      setDeleteDialogOpen(false);
      setSelectionModel([]);
      showSuccess('Property group deleted successfully');
      await fetchGroups();
    } catch (error) {
      console.error('Error deleting property group:', error);
      showError(error.message || 'Failed to delete property group');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    { field: 'groupName', headerName: 'Group Name', flex: 1.2, minWidth: 180 },
    { field: 'groupCode', headerName: 'Group Code', flex: 0.8, minWidth: 140 },
    { field: 'description', headerName: 'Description', flex: 1.4, minWidth: 220 },
    { field: 'status', headerName: 'Status', width: 120 },
  ];

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Property Group
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canEdit ? 'Edit Property Group' : 'No edit permission'}>
            <span>
              <IconButton onClick={openEdit} disabled={!hasSelected || !canEdit} sx={harmonatePlainIconButtonSx}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canAdd ? 'Add Property Group' : 'No add permission'}>
            <span>
              <IconButton onClick={openAdd} disabled={!canAdd} sx={harmonatePlainIconButtonSx}>
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete Property Group' : 'No delete permission'}>
            <span>
              <IconButton
                onClick={() => setDeleteDialogOpen(true)}
                disabled={!hasSelected || !canDelete}
                sx={harmonatePlainIconButtonSx}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          rowHeight={40}
          columnResizeMode="onEnd"
          resizable
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(model) => setSelectionModel(model)}
          pagination
          pageSize={25}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={harmonateDataGridSx}
        />
      </Paper>

      <Dialog
        open={openDialog}
        onClose={() => !submitting && setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          {mode === 'add' ? 'Add Property Group' : 'Edit Property Group'}
          <IconButton onClick={() => !submitting && setOpenDialog(false)} sx={harmonateDialogCloseIconSx} disabled={submitting}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Group Name"
                value={form.groupName}
                onChange={(e) => setForm({ ...form, groupName: e.target.value })}
                size="small"
                fullWidth
                required
                disabled={submitting}
              />
              <TextField
                label="Group Code"
                value={form.groupCode}
                onChange={(e) => setForm({ ...form, groupCode: e.target.value })}
                size="small"
                fullWidth
                disabled={submitting}
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                size="small"
                fullWidth
                multiline
                rows={3}
                disabled={submitting}
              />
              <TextField
                label="Status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                size="small"
                fullWidth
                select
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                disabled={submitting}
              >
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setOpenDialog(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} variant="contained" sx={harmonateProceedButtonSx}>
              {submitting ? 'Saving...' : mode === 'add' ? 'Add Property Group' : 'Update Property Group'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Are you sure you want to delete property group "{selectedRow?.groupName}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button onClick={handleDelete} disabled={deleteLoading} variant="contained" sx={harmonateAbortButtonSx}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast open={toastState.open} message={toastState.message} severity={toastState.severity} onClose={hideToast} />
    </Box>
  );
};

export default PropertyGroup;
